import { useState, useEffect } from 'react';

import { Box, TextField, Autocomplete } from '@mui/material';
import { useDocTypes } from '@/providers/DocTypesContext';
import INSPECTION_TYPES from '@/components/inspectionTypes';
import type { DocumentType } from '@/types/documentTypes';
import type { EntityId } from '@/types/apiTypes';

interface InspectionDocumentTypeFieldProps {
  inspection_type: INSPECTION_TYPES;
  value: { entity_id: EntityId } | undefined;
  setValue: (value: DocumentType | null | undefined) => void;
  setChanged: () => void;
  edit: boolean;
}

function InspectionDocumentTypeField({
  inspection_type,
  value,
  setValue,
  setChanged,
  edit,
}: InspectionDocumentTypeFieldProps): React.JSX.Element {
  const doc_types = useDocTypes();

  const [selected, setSelected] = useState<DocumentType | null>(null);
  const [options, setOptions] = useState<DocumentType[] | null>(null);

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: DocumentType | null,
  ) => {
    setChanged();
    setSelected(newValue);
    setValue(newValue || undefined);
  };

  useEffect(() => {
    if (doc_types && !options) {
      const filteredArray = doc_types.filter(
        (doc_type) => doc_type.enable_manifests,
      );
      setOptions(filteredArray);

      if (value) {
        const full_value = filteredArray.find(
          (doc_type) => doc_type.entity_id === value.entity_id,
        );
        setSelected(full_value || null);
        setValue(full_value || null);
      }
    }
  }, [doc_types, options, value, setValue]);

  return (
    <Box>
      <Autocomplete
        id='document-types-field'
        options={options || []}
        value={selected ?? undefined}
        sx={{ width: '100%' }}
        disabled={edit}
        onChange={handleChange}
        getOptionLabel={(option) => `${option.name}`}
        disableClearable={true}
        renderInput={(params) => (
          <TextField
            {...params}
            required
            label={
              inspection_type === INSPECTION_TYPES.EFFECTIVE
                ? 'Document Type to Inspect'
                : 'Uploaded Document Type'
            }
            helperText='Select the type of the uploaded document. Design History Files include product quality documents such as verification plans, reports, and more.'
          />
        )}
      />
    </Box>
  );
}

export default InspectionDocumentTypeField;

